import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import * as React from "react";

import { ProgressBar } from "./progress-bar.tsx";
import { StepperCircle } from "./stepper-circle.tsx";
import { SubstepTitle } from "./substep-title.tsx";
import { StepTitleProps } from "./types.ts";
import { getStepperSizeConfig } from "./stepper-config.ts";

const SUBSTEP_HEIGHT = 40; // px – anpassen je nach Layout
const TITLE_HEIGHT = 40; // px – falls du den Titel einrechnen willst
const GAP_HEIGHT = 16; // px – optionaler Abstand

export function StepTitle({ step, title, expandable, expanded, complete, active, isLast = false, substeps = [], onToggleExpand, onStepClick, onSubstepClick, stepperSize = 'md', navigable = true, highlightIncomplete = false, neutralDisplay = false }: StepTitleProps) {
  const sizeConfig = getStepperSizeConfig(stepperSize);
  const accordionContentRef = React.useRef<HTMLDivElement>(null);
  const accordionSubstepCollapsedRef = React.useRef<HTMLDivElement>(null);

  const [progressBarHeight, setProgressBarHeight] = React.useState(0);

  // Calculate progress based on substeps
  const calculateProgress = () => {
    if (!substeps.length) {
      return complete ? 100 : 0;
    }
    const completedSubsteps = substeps.filter((substep) => substep.complete).length;
    return (completedSubsteps / substeps.length) * 100;
  };

  // Check if step is truly complete (all substeps done)
  const isStepComplete = () => {
    if (!substeps.length) {
      return complete;
    }
    return substeps.every((substep) => substep.complete);
  };

  const getStepState = (): "inactive" | "active" | "complete" | "incomplete" => {
    // If neutralDisplay is true, only show active or inactive states
    if (neutralDisplay || step.neutralDisplay) {
      if (active) return "active";
      return "inactive";
    }
    
    if (highlightIncomplete && !isStepComplete()) return "incomplete";
    if (isStepComplete()) return "complete";
    if (active) return "active";
    return "inactive";
  };

  const getTitleColor = () => {
    if (!navigable) {
      return "#adb5bd"; // disabled/non-navigable color
    }
    // Skip highlight colors for neutral display steps
    if (!(neutralDisplay || step.neutralDisplay) && highlightIncomplete && !isStepComplete()) {
      return "#dc3545"; // text-danger equivalent (red)
    }
    if (active) {
      return "#212529"; // text-dark equivalent
    }
    return "#6c757d"; // text-muted equivalent
  };

  const getCursorStyle = () => {
    return navigable ? "pointer" : "not-allowed";
  };

  // Calculate progress bar height based on accordion content
  React.useEffect(() => {
    if (!isLast) {
      if (expanded && accordionContentRef.current) {
        // When expanded, measure the accordion content height
        const contentHeight = accordionContentRef.current.offsetHeight;
        // Base height: title height + content height + gaps
        const calculatedHeight = contentHeight + 32; // 40px for title, 16px for spacing
        setProgressBarHeight(calculatedHeight);
      } else if (expandable && !expanded && accordionSubstepCollapsedRef.current) {
        const collapsedSubstepHeight = accordionSubstepCollapsedRef.current.offsetHeight;
        setProgressBarHeight(collapsedSubstepHeight + 4);
      } else {
        // When collapsed, use base height
        setProgressBarHeight(24);
      }
    }
  }, [expanded, substeps, isLast]);

  const progress = calculateProgress();
  const stepState = getStepState();

  const renderContent = () => {
    if (expandable) {
      return (
        <Accordion
          expanded={expanded}
          onChange={onToggleExpand}
          elevation={0}
          sx={{
            //backgroundColor: "blue",
            "&:before": { display: "none" },
            boxShadow: "none",
          }}
        >
          <AccordionSummary
            expandIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            sx={{
              /* Seitenabstände & eigene Hintergrundfarbe */
              padding: 0,

              /* min-height der Root im normalen UND Expanded-Zustand killen */
              minHeight: "auto",
              "&.Mui-expanded": {
                minHeight: "auto",
              },

              /* Margin des Content-Wrappers killen – auch im Expanded-Zustand */
              "& .MuiAccordionSummary-content": {
                margin: 0,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: 0,
              },

              /* Icon-Wrapper unverändert lassen */
              "& .MuiAccordionSummary-expandIconWrapper": {
                transform: "none",
                "&.Mui-expanded": { transform: "none" },
              },
            }}
          >
            <Typography
              variant="body1"
              onClick={(e) => {
                e.stopPropagation();
                if (navigable) {
                  onStepClick?.(step);
                }
              }}
              sx={{
                fontSize: sizeConfig.titleFontSize,
                fontWeight: 400,
                color: getTitleColor(),
                fontFamily: "Inter, sans-serif",
                cursor: getCursorStyle(),
                opacity: navigable ? 1 : 0.6,
                "&:hover": {
                  opacity: navigable ? 0.8 : 0.6,
                },
              }}
            >
              {title}
            </Typography>
          </AccordionSummary>
          {expanded && (
            <AccordionDetails
              sx={{
                //backgroundColor: "red",
                height: "100%",
                padding: 0,
                paddingTop: 2,
                display: "flex",
                flexDirection: "row",
                gap: 1,
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Box
                ref={accordionContentRef}
                sx={{
                  //backgroundColor: "red",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {substeps.map((substep) => (
                  <SubstepTitle key={substep.id} substep={substep} title={substep.title} complete={substep.complete} active={substep.active} onSubstepClick={onSubstepClick} stepperSize={stepperSize} navigable={substep.navigable} highlightIncomplete={highlightIncomplete} />
                ))}
              </Box>
            </AccordionDetails>
          )}
        </Accordion>
      );
    }

    return (
      <Typography
        variant="body1"
        onClick={() => {
          if (navigable) {
            onStepClick?.(step);
          }
        }}
        sx={{
          fontSize: sizeConfig.titleFontSize,
          fontWeight: 500,
          lineHeight: `${sizeConfig.titleFontSize + 10}px`,
          color: getTitleColor(),
          fontFamily: "Inter, sans-serif",
          padding: "2px 10px",
          cursor: getCursorStyle(),
          opacity: navigable ? 1 : 0.6,
          "&:hover": {
            opacity: navigable ? 0.8 : 0.6,
          },
        }}
      >
        {title}
      </Typography>
    );
  };

  const renderCollapsedSubstepsInfo = () => {
    if (!expandable || expanded || !substeps.length) {
      return null;
    }

    return (
      <Typography
        ref={accordionSubstepCollapsedRef}
        variant="body2"
        sx={{
          //backgroundColor: "red",
          fontSize: sizeConfig.substepFontSize,
          fontWeight: 500,
          lineHeight: `${sizeConfig.substepFontSize + 9}px`,
          color: "#6c757d", // text-muted equivalent
          fontFamily: "Inter, sans-serif",
          padding: 2,
        }}
      >
        {substeps.length} Schritte
      </Typography>
    );
  };

  return (
    <Box sx={{ display: "flex", gap: sizeConfig.gap / 8, alignItems: "flex-start" }}>
      {/* Content side */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flex: 1 }}>
        {renderContent()}
        {renderCollapsedSubstepsInfo()}
      </Box>

      {/* Circle and progress bar side */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          isolation: "isolate",
        }}
      >
        <StepperCircle state={stepState} stepperSize={stepperSize} />
        {!isLast && <ProgressBar progress={progress} height={progressBarHeight} active={active} complete={isStepComplete()} stepperSize={stepperSize} />}
      </Box>
    </Box>
  );
}